<script setup>
// Libraries
import { storeToRefs } from 'pinia';

// Stores
import { useWindowStore } from '@/stores/window';

import footer from '@/assets/cms/footer.json';
import { useFooterStore } from '@/stores/footer';
import { useSocialStore } from '@/stores/social';

const currentYear = useState('currentYear', () => new Date().getFullYear())

const props = defineProps({
    pageTitle: {
        type: String,
        required: true,
    },
    previewing: {
        type: Boolean,
        required: false,
        default: false
    },
    noSignUp: {
        type: Boolean,
        default: false,
    },
});

const { innerWidth } = storeToRefs(useWindowStore());

async function manageCookies() {
    var _hsp = (window._hsp = window._hsp || []);
    _hsp.push(['showBanner']);
}
const footerStore = useFooterStore();
const socialStore = useSocialStore();
const socialLinks = computed(() => {
    return socialStore.socials?.socials;
});

const { cta, footerLinks, androidAppUrl, appleAppUrl, newsletter } = toRefs(footerStore);

</script>

<template>
    <footer class="global-footer">
        <section class="container footer-wrapper fs-13">
            <div class="footer-row">
                <div class="inbound-info">
                    <MessLink href="/" class="logo" label="Go to the home page">
                        <MessSvg type="Logo2025Short" />
                    </MessLink>
                    <MessLink :href="cta.url" :title="cta.title" class="register-button fs-13" :label="cta.label">
                        <template v-if="cta.shortLabel">
                            <span class='desktop'>{{ cta.label }}</span>
                            <span class='mobile'>{{ cta.shortLabel }}</span>
                        </template>
                        <span v-else>{{ cta.label }}</span>
                    </MessLink>
                </div>
                <div class="column-wrapper">
                    <div class="socials desktop">
                        <p class="hashtag">#inbound25</p>
                        <ul class="social-links links">
                            <li v-for="(socialLink, index) in socialLinks" :key="index">
                                <MessLink :href="socialLink.url" :label="`Visit INBOUNDs ${socialLink.type} page`">
                                    <MessSvg :type="socialLink.type" />
                                </MessLink>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div class="footer-row" v-if="newsletter">
                <NewsletterSignup :headline="newsletter.headline" :subHead="newsletter.subHead" :subText="newsletter.subText" :buttonText="newsletter.buttonText" :pageTitle="pageTitle" v-if="!noSignUp" footer reducePadding />
            </div>

            <div class="footer-row desktop">
                <div v-if="!androidAppUrl && !appleAppUrl" class="hubspot-info">
                    <MessLink href="https://www.hubspot.com/" class="logo" label="Go to Hubspot website">
                        <span class="sr-only">Go to Hubspot website</span>
                        <MessSvg type="LogoHubspot" />
                    </MessLink>
                    <p class="copyright fs-11">© {{ currentYear }} HubSpot, Inc.</p>
                    <ClientOnly>
                        <ul class="hs-footer-links-wrapper portal-links" id="hubspot-links-wrapper-desktop" v-hidden="innerWidth <= 1040"></ul>
                    </ClientOnly>
                </div>
            </div>

            <div class="footer-row mobile">
                <div v-if="appleAppUrl || androidAppUrl" class="app-links-mobile">
                    <MessLink v-if="appleAppUrl" :href="appleAppUrl" class="apple" label="Go to the iOS App Store to download the INBOUND app">
                        <MessSvg type="appAppleWhite" />
                    </MessLink>
                    <MessLink v-if="androidAppUrl" :href="androidAppUrl" class="android" label="Go to the Google Play Store to download the INBOUND app">
                        <MessSvg type="appAndroidWhite" />
                    </MessLink>
                </div>

                <div class="socials">
                    <p class="hashtag">#inbound25</p>
                    <ul class="social-links links">
                        <li v-for="(socialLink, index) in socialLinks" :key="index">
                            <MessLink :href="socialLink.url" :label="`Visit INBOUNDs ${socialLink.type} page`">
                                <MessSvg :type="socialLink.type" />
                            </MessLink>
                        </li>
                    </ul>
                </div>

                <div class="hubspot-info">
                    <ClientOnly>
                        <ul class="hs-footer-links-wrapper portal-links" v-hidden="innerWidth >= 1041">
                            <Teleport to="#hubspot-links-wrapper-desktop" :disabled="innerWidth <= 1040">
                                <li class="hs-footer-cookie-settings fs-11">
                                    <a href="" class="fs-11" aria-label="Open Cookie Settings Dialog"></a>
                                </li>
                                <li v-for="(footerLink, index) in footerLinks" :key="index">
                                    <MessLink :href="footerLink.url" :label="footerLink.label">{{ footerLink.label }}</MessLink>
                                </li>
                            </Teleport>
                        </ul>
                    </ClientOnly>
                    <div class='logo-line'>
                        <MessLink href="https://www.hubspot.com/" class="logo" label="Go to the Hubspot website">
                            <span class="sr-only">Go to Hubspot website</span>
                            <MessSvg type="LogoHubspot" />
                        </MessLink>
                        <p class="copyright fs-11">© {{ currentYear }} HubSpot, Inc.</p>
                    </div>
                </div>
            </div>
        </section>
    </footer>
</template>

<style lang="scss">
.global-footer {
    background: var(--teal);

    background-image: url('@/assets/img/backgrounds/bubble-teal-6-small.png');
    background-position: right bottom;
    background-repeat: no-repeat;

    color: var(--cream, #ffffff);
    padding: 3rem 0;

    @media (min-width: $tablet) {
        background-image: url('@/assets/img/backgrounds/bubble-3-footer.png');
        background-position: right bottom;
        background-repeat: no-repeat;
    }

    a {
        color: var(--cream, #ffffff);
    }

    .register-button {
        text-transform: uppercase;
        letter-spacing: 0.0375rem;
        border-radius: var(--radius);
        background-color: var(--orange);
        color: white;
        padding: 15px;
        font-weight: normal;

        &:hover,
        &:focus {
            background-color: var(--cream);
            color: var(--orange);
        }
    }

    .footer-wrapper {
        display: flex;
        flex-direction: column;
        gap: 2rem;
        justify-content: space-between;

        //mobile
        .footer-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 2rem;

            @media (min-width: $tablet) {
                margin-bottom: var(--box-padding-tb);
            }

            .apple {}

            .android {
                margin-right: auto;
            }

            ul:empty {
                display: none;
            }

            .column-wrapper {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 1rem;

                @media (max-width: $tablet) {
                    flex-direction: column;
                    gap: 1.5rem;
                }
            }

            .links {
                display: flex;
            }

            .speaker-links {
                color: var(--cream, #ffffff);
                font-weight: 600;
                line-height: normal;
                letter-spacing: 1.3px;
                text-transform: uppercase;
                display: flex;
                gap: 1.75rem;
                flex-wrap: wrap;

                @media (max-width: $mobile) {
                    letter-spacing: 0px;
                }

                a {
                    transition: 250ms ease;

                    &:hover,
                    &.mess-active-link {
                        color: var(--Solaris-Original, #ff5c35);
                    }
                }
            }

            .register {
                color: white;

                &:hover {
                    transition: 250ms ease;
                    color: var(--teal, #042729);
                }
            }

            .inbound-info {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 1.25rem;

                .event-details {
                    align-self: center;
                    color: var(--Neutrals-Milky-Way, #b6c7d6);
                    text-align: right;
                    font-style: normal;
                    font-weight: 600;
                    line-height: normal;
                    letter-spacing: 1.3px;
                    text-transform: uppercase;

                    @media (max-width: $mobile) {
                        letter-spacing: 0px;
                    }
                }
            }

            .logo-line {
                display: flex;
                align-items: center;
                gap: 1.5rem;
            }

            .hubspot-info {
                display: flex;
                flex-wrap: wrap;
                flex-direction: column;
                align-items: start;
                gap: 1.5rem;
                color: var(--cream, #ffffff);
                width: 100%;

                @media (min-width: $tablet) {
                    flex-direction: row;
                    align-items: center;
                }

                .hs-footer-links-wrapper {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 1.5rem;

                    &:empty {
                        display: none;
                    }

                    li {
                        width: max-content;
                        line-height: 0;
                        &:has(a:empty) {
                            display: none;
                        }
                        & a:empty {
                            display: none;
                        }
                    }

                    .hs-footer-cookie-settings {
                        a {
                            border-bottom: 1px solid var(--cream);

                            &:hover {
                                transition: 250ms ease;
                                color: var(--Solaris-Medium);
                                border-bottom: 1px solid var(--Solaris-Medium);
                            }
                        }
                    }
                }

                .copyright {
                    font-style: normal;
                    font-weight: 300;
                    letter-spacing: 0.315px;
                    color: var(--sage);
                }

                .manage-cookies,
                .privacy-policy {
                    font-weight: 300;
                    letter-spacing: 0.315px;
                    border-bottom: 1px solid var(--cream);
                    color: var(--sage);

                    // margin-left: 2rem;
                    &:hover {
                        transition: 250ms ease;
                        color: var(--cream);
                        border-bottom: 1px solid var(--cream);
                    }

                    a {
                        color: var(--sage);
                        &:hover {
                            color: var(--cream);
                        }
                    }

                    @media (max-width: $tablet) {
                        margin: 0;
                    }
                }

                @media (max-width: $tablet) {
                    justify-content: center;
                }
            }

            .portal-links {
                display: flex;
                gap: 1.5rem;
                font-weight: 300;
                letter-spacing: 0.39px;

                a {
                    transition: 250ms ease;
                    color: white;
                    border-bottom: 1px solid white;
                    font-size: calc(11 / var(--base-fs) * 1rem);

                    &:hover,
                    &.mess-active-link {
                        color: var(--sage);
                        border-bottom: 1px solid var(--sage);
                    }
                }


                .hs-opt-out-settings a {
                    border-bottom: 1px solid transparent;
                }
            }

            .socials {
                display: flex;
                flex-direction: column;
                flex-wrap: wrap;
                align-items: start;
                gap: 1.25rem;
                width: 100%;

                .hashtag {
                    color: var(--cream);
                    font-size: 1rem;
                    font-style: normal;
                    font-weight: 600;
                    line-height: normal;
                    letter-spacing: 0.02rem;
                    text-transform: uppercase;

                    @media (min-width: $tablet) {
                        font-size: 0.8125rem;
                        letter-spacing: 0.08125rem;
                    }
                }

                @media (min-width: $tablet) {
                    flex-direction: row;
                    gap: 1.5rem;
                    align-items: center;
                }

                .social-links {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 1rem;

                    svg {
                        width: 2.5rem;
                        height: 2.5rem;

                        @media (min-width: $tablet) {
                            width: 2.25rem;
                            height: 2.25rem;    
                        }

                        circle {
                            fill: black;
                            stroke-width: 0;
                        }
                        path {
                            fill: white;
                        }
                    }

                    a:hover, a:focus {
                        svg {
                            circle {
                                fill: var(--orange);
                            }
                            path {
                                fill: white;
                            }
                        }
                    }
                }
            }

            @media (max-width: $tablet) {
                flex-direction: column;
                gap: 1.5rem;
            }
        }

        @media (max-width: $tablet) {
            gap: 1.5rem;
        }
    }

    @media (min-width: 1041px) {
        .mobile {
            display: none !important;
        }

        .desktop {
            display: flex;
        }
    }

    @media (max-width: $tablet) {

        .footer-wrapper .footer-row .inbound-info {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: center;
            width: 100%;

            .logo {
                display: none;
            }

            .register-button {
                width: 100%;
                text-align: center;
                span {
                    display: inline-block;
                    width: 100%;
                    text-align: center;
                }
            }

        }

        .mobile {
            display: flex;

            .app-links-mobile {
                display: flex;
                gap: 2rem;
            }
        }

        .desktop {
            display: none !important;
        }
    }
}
</style>
